import { Stack } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import 'dayjs/locale/en-gb';
import { useContext, useEffect, useState } from 'react';
import { OverallDataContext } from '../../pages/CalculatorPage';

// Current version implemented as a date range picker
export default function DatePickerField({ id_name, category, checkInputValue, isRequired, errorMsg, setFocus, isReadOnly }) {
    const placeholderData = useContext(OverallDataContext).placeholderData;
    const calcStage = useContext(OverallDataContext).calcStage[0];

    const [touched, setTouched] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const assignRangeValues = (dateStringArray) => {
        if (!dateStringArray) {
            setStartDate(null);
            setEndDate(null);
            return;
        }
    
        const [start, end] = dateStringArray.map(date =>
            dayjs(date).isValid() ? dayjs(date) : null
        );
        setStartDate(start);
        setEndDate(end);
    };

    // Determines which data source to use
    const getStateValue = () => {
        let rawValue = placeholderData?.[category]?.[id_name];
        rawValue = typeof rawValue === "string" ? rawValue?.replace(/'/g, '"') : null
        return JSON.parse(rawValue || '[]');
    };

    const stringifyDate = (dayJSDate) => {
        try {
            return dayJSDate?.toISOString();
        } catch {
            return null;
        }
    }
    
    // Effect to update state when `placeholderData` changes
    useEffect(() => {
        setTouched(false);
        if (calcStage === 1 ) assignRangeValues(); // Reset values
        else {
            assignRangeValues(getStateValue());
            setTouched(true);
        }
}, [calcStage, placeholderData, id_name]); // Only update when these change

    // Effect to trigger validation when the date changes
    useEffect(() => {
        if (touched) {
            checkInputValue(id_name, [stringifyDate(startDate), stringifyDate(endDate)]);
        }
    }, [touched, startDate, endDate]); 

    return (
        <Stack spacing={1} direction={{ xs: "column", sm: "row"}} sx={{ width: '100%', textAlign: 'center' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb' >
            {/* Start Date Picker */}
                <DatePicker
                    value={startDate}
                    onChange={(newValue) => {
                        setStartDate(newValue);
                        // Ensure end date is not before start date
                        if (endDate && newValue && dayjs(newValue).isAfter(endDate)) {
                        setEndDate(null);
                        }
                    }}
                    slotProps={{
                        textField : {
                            name: `tf${id_name}_start`,
                            id: `tf${id_name}_start`,
                            onFocus: () => setFocus(true),
                            onBlur: () => {setFocus(false); setTouched(true);},
                            required: isRequired,
                            disabled: isReadOnly,
                            error: (errorMsg !== ''),
                            helperText: errorMsg,
                            fullWidth: true
                        }
                    }}
                    onOpen={() => setFocus(true)}
                    onClose={() => {setFocus(false); setTouched(true);}}
                />

                <span style={{ marginTop: 8, fontSize: 'small' }}> to </span>

                {/* End Date Picker */}
                <DatePicker
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    minDate={startDate} // Disable dates before start date
                    slotProps={{
                        textField : {
                            name: `tf${id_name}_end`,
                            id: `tf${id_name}_end`,
                            onFocus: () => setFocus(true),
                            onBlur: () => {setFocus(false); setTouched(true);},
                            required: isRequired,
                            disabled: isReadOnly,
                            error: (errorMsg !== ''),
                            helperText: errorMsg,
                            fullWidth: true
                        }
                    }}
                    onOpen={() => setFocus(true)}
                    onClose={() => {setFocus(false); setTouched(true);}}
                />
            </LocalizationProvider>
        </Stack>
    )
}